import { ArrowRight, Layers, Plus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { Customer } from '@/features/crm/types'
import type { CatalogModuleView, ProductPackage, ProjectSummary, Sector } from '../../types'
import { PriceLine, ProjectIcon, ProjectStatusBadge } from '../ProjectBits'
import type { IntegrateTarget } from './IntegrateModal'

type Props = {
  projects: ProjectSummary[]
  packages: ProductPackage[]
  catalog: CatalogModuleView[]
  customers: Customer[]
  sectors: Sector[]
  canWrite: boolean
  onShowModules: (projectId: string) => void
  onIntegrate: (target: IntegrateTarget) => void
}

export function ProductPackages({ projects, packages, catalog, customers, sectors: allSectors, canWrite, onShowModules, onIntegrate }: Props) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
      {projects.map((p) => {
        const pkg = packages.find((x) => x.projectId === p.id)
        const required = catalog.filter((m) => m.usage.some((u) => u.projectId === p.id && u.requirement === 'required'))
        const mine = customers.filter((c) => c.projectId === p.id)
        const live = mine.filter((c) => c.stage === 'customer').length
        const sectors = allSectors.filter((s) => s.projectIds.includes(p.id))
        return (
          <article key={p.id} className="flex flex-col gap-4 rounded-xl border bg-card p-4 shadow-card">
            <header className="flex items-start gap-3">
              <ProjectIcon icon={p.icon} color={p.color} className="size-11" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold">{p.name}</h3>
                  <ProjectStatusBadge status={p.status} />
                </div>
                <p className="font-mono text-xs text-muted-foreground">
                  {p.key}
                  {p.version ? ` · v${p.version}` : ''}
                </p>
              </div>
            </header>

            {sectors.length ? (
              <div className="flex flex-wrap gap-1.5">
                {sectors.map((s) => (
                  <Badge key={s.id} tone="outline">
                    {s.name}
                  </Badge>
                ))}
              </div>
            ) : null}

            <div className="space-y-2 rounded-lg bg-muted/50 p-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs text-muted-foreground">{t('projects.catalog.packages.base', { count: pkg?.requiredCount ?? 0 })}</span>
                <span className="text-lg font-semibold">{pkg ? <PriceLine amounts={pkg.base} /> : '—'}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t pt-2">
                <span className="text-xs text-muted-foreground">{t('projects.catalog.packages.addons', { count: pkg?.optionalCount ?? 0 })}</span>
                <span className="text-sm font-medium">{pkg ? <PriceLine amounts={pkg.addons} /> : '—'}</span>
              </div>
            </div>

            {required.length ? (
              <p className="text-xs text-muted-foreground">
                {t('projects.catalog.packages.includes')}: <span className="text-foreground">{required.map((m) => m.name).join(', ')}</span>
              </p>
            ) : (
              <p className="text-xs text-warning">{t('projects.catalog.packages.noBase')}</p>
            )}

            <div className="mt-auto flex flex-wrap items-center gap-2 border-t pt-3">
              <Link to="/customers/list" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
                <Users className="size-3.5" aria-hidden />
                {t('projects.catalog.packages.customers', { count: mine.length, live })}
              </Link>
              <div className="ml-auto flex flex-wrap gap-1.5">
                {canWrite ? (
                  <Button size="sm" variant="outline" onClick={() => onIntegrate({ kind: 'create', projectId: p.id })}>
                    <Plus />
                    {t('projects.catalog.packages.addModule')}
                  </Button>
                ) : null}
                <Button size="sm" variant="ghost" onClick={() => onShowModules(p.id)}>
                  <Layers />
                  {t('projects.catalog.packages.showModules')}
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to={`/projects/${p.id}`}>
                    {t('projects.catalog.packages.openProject')}
                    <ArrowRight />
                  </Link>
                </Button>
              </div>
            </div>
          </article>
        )
      })}
    </div>
  )
}
